import type { ActionIconProps, DefaultMantineColor, TextProps } from '@mantine/core'
import { ActionIcon, Box, Button, Group, Text, Textarea, Tooltip } from '@mantine/core'
import { MIME_TYPES } from '@mantine/dropzone'
import { useField } from '@mantine/form'
import { useDebouncedCallback, useDisclosure, useHotkeys } from '@mantine/hooks'
import {
  downloadDataAsFile,
  formatDate,
  getObjectKeys,
  type TypeOrTypeFn,
  useAuthorization,
  useGlobalState,
} from '@opsopti/shared'
import type { IconProps } from '@tabler/icons-react'
import {
  IconArchive,
  IconArrowBack,
  IconArrowUpRight,
  IconBrowserPlus,
  IconChevronDown,
  IconChevronUp,
  IconCircleX,
  IconCsv,
  IconDeviceFloppy,
  IconEye,
  IconHelp,
  IconLockShare,
  IconPencil,
  IconPlayerPlay,
  IconSearch,
  IconTableImport,
  IconTrash,
  IconX,
  IconZoomCancel,
} from '@tabler/icons-react'
import merge from 'deepmerge'
import type {
  MRT_Cell,
  MRT_Column,
  MRT_Row,
  MRT_RowData,
  MRT_TableInstance,
  MRT_TableOptions,
} from 'mantine-react-table'
import { MantineReactTable, MRT_GlobalFilterTextInput, useMantineReactTable } from 'mantine-react-table'
import { observer } from 'mobx-react-lite'
import Papa from 'papaparse'
import pluralize from 'pluralize'
import type { ComponentType } from 'react'
import { memo, useEffect, useImperativeHandle, useMemo, useState } from 'react'

import type { EmptyStateProps } from '@/components/EmptyState'
import { EmptyState } from '@/components/EmptyState'
import { theme } from '@/configs/theme'
import * as classes from '@/styles/Table.css'

const rowActionsIconRegistry = {
  edit: IconPencil,
  view: IconEye,
  delete: IconTrash,
  archive: IconArchive,
  demo: IconPlayerPlay,
  resetPassword: IconLockShare,
  assign: IconArrowUpRight,
  assignToDefault: IconArrowBack,
}

const rowActionsIconColorMap: Record<keyof typeof rowActionsIconRegistry, string> = {
  edit: 'gray.8',
  view: 'blue',
  delete: 'red.7',
  archive: 'red.7',
  demo: 'blue',
  resetPassword: 'indigo',
  assign: 'cornflower',
  assignToDefault: 'port.7',
}

const toolBarActionsLabelMap = {
  new: 'New',
  import: 'Import',
  heightToggle: 'Full Height',
  export: 'Export',
}

export interface TableProps<DataT extends MRT_RowData> extends MRT_TableOptions<DataT> {
  tableRef?: React.RefObject<MRT_TableInstance<DataT> | null>
  entity?: { singular: string; plural: string; Icon?: ComponentType<IconProps> }
  rowActionsInclude?: TableRowActionsProps<DataT>['include']
  rowActionsTooltipLabels?: TableRowActionsProps<DataT>['tooltipLabels']
  rowActionsButtonProps?: TableRowActionsProps<DataT>['actionButtonProps']
  onRowActionClick?: TableRowActionsProps<DataT>['onActionClick']
  toolbarActionsInclude?: TableTopToolbarProps<DataT>['include']
  toolbarActionsState?: TableTopToolbarProps<DataT>['state']
  onToolbarActionClick?: TableTopToolbarProps<DataT>['onActionClick']
  EmptyStateProps?: TableEmptyRowsFallbackProps<DataT>['EmptyStateProps']
}

export function Table<DataT extends MRT_RowData>(props: TableProps<DataT>) {
  const {
    tableRef,
    data,
    columns,
    rowActionsInclude,
    onRowActionClick,
    renderRowActions,
    entity,
    rowActionsButtonProps,
    onToolbarActionClick,
    toolbarActionsInclude,
    toolbarActionsState,
    EmptyStateProps,
    rowActionsTooltipLabels,
    ...additionalConfig
  } = props

  const config = useMemo(
    () =>
      merge.all<Omit<MRT_TableOptions<DataT>, 'data' | 'columns'>>([
        {
          layoutMode: 'grid',
          enableFilterMatchHighlighting: false,
          enableBottomToolbar: false,
          enableColumnActions: false,
          enableSorting: true,
          enableColumnFilters: true,
          columnFilterDisplayMode: 'subheader',
          enablePagination: false,
          enableHiding: false,
          enableDensityToggle: false,
          enableFullScreenToggle: false,
          enableGlobalFilter: true,
          paginationDisplayMode: 'pages',
          rowVirtualizerOptions: { overscan: 15 },
          manualPagination: true,
          mantinePaginationProps: { showRowsPerPage: false },
          positionGlobalFilter: 'left',
          initialState: { density: 'md', showGlobalFilter: true, showColumnFilters: true },
          mantinePaperProps: { shadow: 'none', withBorder: false, radius: 0, className: classes.paper },
          mantineTableBodyRowProps: { className: classes.rowDetailPanel },
          mantineDetailPanelProps: { style: classes.tdDetailPanelStyles },
          mantineTableBodyProps: { className: classes.body },
          mantineTableBodyCellProps: { className: classes.td },
          mantineTableContainerProps: { className: classes.container },
          mantineTableProps: { highlightOnHover: true },
          positionActionsColumn: 'last',
          mantineFilterTextInputProps: { style: { borderBottom: 'unset' }, variant: 'filled', size: 'xs' },
          mantineFilterDateInputProps: { style: { borderBottom: 'unset' }, variant: 'filled', size: 'xs' },
          mantineFilterSelectProps: {
            style: { borderBottom: 'unset' },
            variant: 'filled',
            size: 'xs',
            searchable: false,
          },
          mantineFilterMultiSelectProps: {
            style: { borderBottom: 'unset' },
            variant: 'filled',
            size: 'xs',
            searchable: false,
          },
          mantineFilterCheckboxProps: { size: 'xs', color: 'gray', variant: 'filled' },
          mantineTableHeadCellProps: { classNames: { th: classes.th } },
          displayColumnDefOptions: {
            'mrt-row-actions': {
              header: undefined,
              grow: false,
              size: 140,
            },
            'mrt-row-drag': {
              header: undefined,
            },
            'mrt-row-expand': {
              Header: () => null,
              grow: false,
              size: 60,
            },
          },
          renderTopToolbar: ({ table }) => (
            <TableTopToolbar
              table={table}
              entity={entity}
              include={toolbarActionsInclude}
              onActionClick={onToolbarActionClick}
              state={toolbarActionsState}
            />
          ),
          renderEmptyRowsFallback: ({ table }) => (
            <TableEmptyRowsFallback table={table} entity={entity} EmptyStateProps={EmptyStateProps} />
          ),
        },
        rowActionsInclude
          ? {
              enableRowActions: true,
              renderRowActions: props => (
                <TableRowActions
                  {...props}
                  include={rowActionsInclude}
                  tooltipLabels={rowActionsTooltipLabels}
                  onActionClick={onRowActionClick}
                  actionButtonProps={rowActionsButtonProps}
                />
              ),
            }
          : {},
        renderRowActions ? { enableRowActions: true, renderRowActions } : {},
        additionalConfig,
      ]),
    [additionalConfig],
  )

  const table = useMantineReactTable({
    data,
    columns,
    ...config,
  })

  useImperativeHandle(tableRef, () => table, [table])

  return <MantineReactTable table={table} />
}

type TableRowActionsProps<DataT extends MRT_RowData> = Parameters<
  NonNullable<TableProps<DataT>['renderRowActions']>
>[0] & {
  include?: TypeOrTypeFn<Array<keyof typeof rowActionsIconRegistry> | undefined, [MRT_Row<DataT>]>
  actionButtonProps?: TypeOrTypeFn<
    Partial<Record<keyof typeof rowActionsIconRegistry, ActionIconProps & { component?: any }>> | undefined,
    [MRT_Row<DataT>]
  >
  onActionClick?: (action: keyof typeof rowActionsIconRegistry, row: MRT_Row<DataT>) => void
  tooltipLabels?: TypeOrTypeFn<
    Partial<Record<keyof typeof rowActionsIconRegistry, string | undefined>> | undefined,
    [MRT_Row<DataT>]
  >
}

export const TableRowActionsComponent = memo(function TableRowActionsComponent<DataT extends MRT_RowData>(
  props: TableRowActionsProps<DataT>,
) {
  const { onActionClick, row } = props

  const include = (typeof props.include === 'function' ? props.include(row) : props.include) ?? []
  const actionButtonProps =
    typeof props.actionButtonProps === 'function' ? props.actionButtonProps(row) : props.actionButtonProps
  const tooltipLabels = typeof props.tooltipLabels === 'function' ? props.tooltipLabels(row) : props.tooltipLabels

  if (!include.length) return null

  return (
    <Group gap="xs" justify="flex-end" flex="1 1 100%" wrap="nowrap">
      {include.map(a => {
        const tooltipLabel = tooltipLabels?.[a]

        return (
          <TableRowActionButton
            key={a}
            action={a}
            tooltipLabel={tooltipLabel}
            onClick={() => onActionClick?.(a, row)}
            actionButtonProps={actionButtonProps?.[a]}
          />
        )
      })}
    </Group>
  )
})

export const TableRowActions = memo(TableRowActionsComponent) as <DataT extends MRT_RowData>(
  props: TableRowActionsProps<DataT>,
) => JSX.Element

export const TableRowActionButton = memo(function TableRowActionButton(props: {
  action: keyof typeof rowActionsIconRegistry
  Icon?: ComponentType<IconProps>
  actionButtonProps?: ActionIconProps & { component?: any }
  tooltipLabel?: string
  color?: DefaultMantineColor
  onClick?: () => void
}) {
  const Icon = props.Icon ?? rowActionsIconRegistry[props.action]
  const color = props.color ?? rowActionsIconColorMap[props.action]

  return (
    <Tooltip
      withArrow
      arrowSize={12}
      position="top"
      offset={8}
      color={color}
      openDelay={250}
      label={props.tooltipLabel}
      disabled={!props.tooltipLabel}
      keepMounted={false}>
      <ActionIcon variant="light" color={color} {...props.actionButtonProps} onClick={props.onClick}>
        <Icon className={classes.rowActionIcon} stroke={1.5} />
      </ActionIcon>
    </Tooltip>
  )
})

type TableTopToolbarProps<DataT extends MRT_RowData> = {
  entity: TableProps<DataT>['entity']
  table: MRT_TableInstance<DataT>
  include?: Array<'new' | 'heightToggle' | 'import' | 'export'>
  onActionClick?: (action: 'new' | 'heightToggle' | 'import' | 'export') => void
  state?: { heightToggle?: 'full' | 'auto' }
}

export function TableTopToolbar<DataT extends MRT_RowData>(props: TableTopToolbarProps<DataT>) {
  const { entity, include = [], onActionClick, table, state } = props
  const { heightToggle = 'auto' } = state ?? {}
  const [httOpen, { close: closeHTT, open: openHTT }] = useDisclosure(false)
  const [numberOfFilters, setNumberOfFilters] = useState<number>(0)

  const handleSearch = useDebouncedCallback(() => {
    const state = table?.getState()

    setNumberOfFilters((!!state.globalFilter ? 1 : 0) + state.columnFilters.length)
  }, 250)

  handleSearch()

  const ImportButton = useMemo(
    // We have to do this because we can't wrap the TableTopToolbar in observe which kills the filter counts.
    () =>
      observer(() => {
        const importEnabled = useAuthorization({ FlagAllowImport: true })

        if (importEnabled) {
          return (
            <Button onClick={() => onActionClick?.('import')} leftSection={<IconTableImport size={24} />}>
              {`${toolBarActionsLabelMap['import']} ${entity ? entity.plural : ''}`}
            </Button>
          )
        } else {
          return null
        }
      }),
    [],
  )

  return (
    <Group justify="space-between" mb={theme.spacing.xl}>
      <Group>
        {table.options.enableGlobalFilter && (
          <MRT_GlobalFilterTextInput
            table={props.table}
            size="sm"
            placeholder={`Search ${entity ? entity.plural : ''}`}
            classNames={{ root: classes.globalFilter, input: classes.globalFilterInput }}
            leftSection={<IconSearch stroke={1.5} />}
          />
        )}

        {table.options.enableColumnFilters && !!numberOfFilters && (
          <Button
            size="sm"
            color="red.9"
            leftSection={<IconX />}
            onClick={() => {
              table.resetGlobalFilter()
              table.resetColumnFilters()
              setNumberOfFilters(0)
            }}>
            Clear {pluralize('Filter', numberOfFilters, true)}
          </Button>
        )}
      </Group>

      {include.length ? (
        <Group>
          {include.map(a => {
            if (a === 'new') {
              return (
                <Button key={a} onClick={() => onActionClick?.(a)} leftSection={<IconBrowserPlus size={24} />}>
                  {`${toolBarActionsLabelMap[a]} ${entity ? entity.singular : ''}`}
                </Button>
              )
            } else if (a === 'import') {
              return <ImportButton key={a} />
            } else if (a === 'export') {
              return (
                <ActionIcon
                  key={a}
                  variant="light"
                  onClick={() => {
                    const rows = table.getPrePaginationRowModel().rows
                    const data = rows.map(r => r.getAllCells())

                    const exportData = data.map(row =>
                      row.reduce((acc, cell) => {
                        const header = cell.column.columnDef.header
                        const value = cell.getValue()

                        if (!header) return acc
                        return { ...acc, [header]: value }
                      }, {}),
                    )

                    if (exportData.length) {
                      const csv = Papa.unparse({ fields: getObjectKeys(exportData[0]), data: exportData })
                      downloadDataAsFile(
                        csv,
                        `Ops-Opti-Export-${entity ? entity.plural : 'Table'}-${formatDate(Date.now(), { format: 'M-D-YYYY-h-mm-ss-A' })}.csv`,
                        MIME_TYPES.csv,
                      )
                    }
                  }}>
                  <IconCsv />
                </ActionIcon>
              )
            } else if (a === 'heightToggle') {
              return (
                <Tooltip
                  opened={httOpen}
                  key={a}
                  withArrow
                  arrowSize={12}
                  position="top"
                  offset={8}
                  color="port.7"
                  keepMounted={false}
                  label={heightToggle === 'auto' ? 'Increase Table Height' : 'Show Page Header'}>
                  <ActionIcon
                    variant="light"
                    onMouseEnter={openHTT}
                    onMouseLeave={closeHTT}
                    onClick={() => {
                      onActionClick?.(a)
                      closeHTT()
                    }}>
                    {heightToggle === 'auto' ? <IconChevronUp /> : <IconChevronDown />}
                  </ActionIcon>
                </Tooltip>
              )
            }

            return null
          })}
        </Group>
      ) : (
        <Box />
      )}
    </Group>
  )
}

type TableEmptyRowsFallbackProps<DataT extends MRT_RowData> = {
  entity: TableProps<DataT>['entity']
  table: MRT_TableInstance<DataT>
  EmptyStateProps?: EmptyStateProps
}

export function TableEmptyRowsFallback<DataT extends MRT_RowData>(props: TableEmptyRowsFallbackProps<DataT>) {
  const { entity, table, EmptyStateProps } = props

  const { globalFilter } = table.getState()

  return (
    <EmptyState
      size="sm"
      Icon={globalFilter ? IconZoomCancel : (entity?.Icon ?? IconCircleX)}
      title={`No ${entity ? entity.plural : 'Records'} to Display`}
      description={globalFilter ? 'Reset the filter or try a different search term.' : undefined}
      py="xxl"
      {...EmptyStateProps}
    />
  )
}

export const HeaderCellWithTooltip = memo(function HeaderCellWithTooltip(props: { column: any; tooltip: string }) {
  return (
    <Tooltip
      withArrow
      arrowSize={12}
      position="top"
      offset={8}
      openDelay={250}
      label={props.tooltip}
      keepMounted={false}>
      <Group gap={theme.rem(4)} justify="flex-start" align="center">
        {props.column.columnDef.header}
        <IconHelp stroke={1.5} size={20} />
      </Group>
    </Tooltip>
  )
})

type CellTextWithTooltipProps<T extends MRT_RowData> = {
  cell: MRT_Cell<T>
  TextProps?: TextProps
}

function CellTextWithTooltipComponent<T extends MRT_RowData>(props: CellTextWithTooltipProps<T>) {
  return (
    <Tooltip
      withArrow
      arrowSize={12}
      position="top"
      offset={8}
      openDelay={250}
      label={props.cell.getValue<string>()}
      multiline
      maw={640}
      keepMounted={false}>
      <Text size="sm" lineClamp={2} {...props.TextProps}>
        {props.cell.getValue<string>()}
      </Text>
    </Tooltip>
  )
}

export const CellTextWithTooltip = memo(CellTextWithTooltipComponent) as <T extends MRT_RowData>(
  props: CellTextWithTooltipProps<T>,
) => JSX.Element

type EditTextareaProps<TData extends MRT_RowData, TValue extends string> = {
  cell: MRT_Cell<TData, TValue>
  column: MRT_Column<TData, TValue>
  row: MRT_Row<TData>
  table: MRT_TableInstance<TData>
  updateFn: (value: string) => Promise<any>
}

function EditTextareaComponent<TData extends MRT_RowData, TValue extends string>(
  props: EditTextareaProps<TData, TValue>,
) {
  const { uiStore } = useGlobalState()
  const field = useField({ initialValue: props.cell.getValue() ?? '' })

  useEffect(() => {
    uiStore.setIsEditingTableCell(true)

    return () => {
      uiStore.setIsEditingTableCell(false)
    }
  }, [])

  function dismiss() {
    props.table.setEditingRow(null)
    props.table.setEditingCell(null)
  }

  useHotkeys([['Escape', dismiss]], [])

  async function save() {
    await props.updateFn(field.getValue())

    dismiss()
  }

  return (
    <Textarea
      {...field.getInputProps()}
      autoFocus
      autosize
      minRows={4}
      size="sm"
      style={{ width: '100%' }}
      onBlur={() => {
        if (!field.isDirty()) dismiss()
      }}
      rightSection={
        <ActionIcon variant="light" onClick={save}>
          <IconDeviceFloppy stroke={1.5} />
        </ActionIcon>
      }
    />
  )
}

export const EditTextarea = memo(EditTextareaComponent) as <TData extends MRT_RowData, TValue extends string>(
  props: EditTextareaProps<TData, TValue>,
) => JSX.Element
